// The well in section: a shaft cut open with the water at the bottom. On the ground, either side, stand temporary tents
// like an open public meet and greet: fundraisers on the left, news on the right (read from the text version). In the
// middle room there is a small tap game: bubbles drift near the bottom, a tap drops the bucket, and on the way up it may
// bring one back. Each bubble carries news, a discount code or an entry code.
// Feet, x along the cut, y up from the ground line (the shaft goes down to -40).
import type { Host, Kit, SectionDef } from './section';

const W = 210;
const CX = 105; // the shaft's centre line
const SX0 = 94, SX1 = 116; // shaft walls
const WATER = -28, FLOOR = -40;
const DIP = -36.5; // the bucket's lowest point (its base)
const REST = 5; // the bucket's base at the top

type Kind = 'news' | 'discount' | 'entry';
interface Prize { kind: Kind; title: string; text: string; code?: string }
interface Bubble { ph: number; w: number; ax: number; by: number; ay: number; r: number; prize: number }

interface Tent { x0: number; w: number; kind: 'fundraiser' | 'news'; title: string; text: string; time: string; col: 0 | 1 | 2 }
const TENT_W = 18;
let tents: Tent[] = [];
const KIND_LABEL: Record<Kind, string> = { news: 'News', discount: 'Discount code', entry: 'Entry code' };

// ---- game state (reset each time the view opens) ----------------------------------
let prizes: Prize[] = [];
let bubbles: Bubble[] = [];
let nextPrize = 0;
let phase: 'idle' | 'down' | 'dip' | 'up' = 'idle';
let pt = 0;
let by = REST;
let caught: { prize: number; r: number } | null = null;
let bursts: { x: number; y: number; t: number; col: string }[] = [];
let ripple = 0;
let cardOpen = false;
let respawn: number[] = [];
let onResult: (p: Prize | null) => void = () => {};

const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

function newBubble(i: number, ph = rnd(0, 6.283)): Bubble {
  return { ph, w: rnd(0.45, 0.95), ax: rnd(2.5, 8.2), by: rnd(-37.4, -32), ay: rnd(0.4, 1.4), r: rnd(1.5, 2.1), prize: i % Math.max(1, prizes.length) };
}
const bubbleAt = (b: Bubble, t: number): { x: number; y: number } => ({ x: CX + b.ax * Math.sin(b.w * t + b.ph), y: b.by + b.ay * Math.sin(b.w * 1.7 * t + b.ph * 1.7) });

function drop(): boolean {
  if (phase !== 'idle' || cardOpen) return false;
  phase = 'down'; pt = 0;
  return true;
}

function update(k: Kit): void {
  const dt = k.dt, t = k.t;
  if (phase === 'idle') by = REST + 0.25 * Math.sin(t * 1.6);
  else if (phase === 'down') {
    pt += dt;
    const u = Math.min(1, pt / 1.2);
    by = REST + (DIP - REST) * Math.pow(u, 1.7);
    if (u >= 1) {
      phase = 'dip'; pt = 0; ripple = 0.001;
      // the catch: a bubble inside the bucket's mouth right now comes up with it
      let best = -1, bd = 1e9;
      bubbles.forEach((b, i) => {
        const p = bubbleAt(b, t);
        const dx = Math.abs(p.x - CX);
        if (dx < 4 + 0.3 * b.r && Math.abs(p.y - (DIP + 1.7)) < 4.2 && dx < bd) { bd = dx; best = i; }
      });
      if (best >= 0) {
        const b = bubbles.splice(best, 1)[0];
        caught = { prize: b.prize, r: b.r };
        respawn.push(t + 2.4);
      }
    }
  } else if (phase === 'dip') {
    pt += dt;
    if (pt > 0.5) { phase = 'up'; pt = 0; }
  } else {
    pt += dt;
    const u = Math.min(1, pt / 1.5);
    by = DIP + (REST - DIP) * (1 - Math.pow(1 - u, 2));
    if (u >= 1) {
      phase = 'idle'; by = REST;
      if (caught) {
        const p = prizes[caught.prize % prizes.length];
        bursts.push({ x: CX, y: REST + 5, t: 0, col: p.kind === 'news' ? k.c.cobalt : p.kind === 'discount' ? k.c.coral : k.c.lime });
        caught = null;
        onResult(p);
      } else onResult(null);
    }
  }
  if (ripple > 0) { ripple += dt; if (ripple > 1.1) ripple = 0; }
  for (const b of bursts) b.t += dt;
  bursts = bursts.filter((b) => b.t < 0.6);
  if (respawn.length && respawn[0] < t) {
    respawn.shift();
    bubbles.push(newBubble(nextPrize++));
  }
}

const withAlpha = (col: string, a: number): string => col.replace(/,\s*[\d.]+\)$/, `,${a})`);
const kindColour = (k: Kit, kind: Kind): string => (kind === 'news' ? k.c.cobalt : kind === 'discount' ? k.c.coral : k.c.lime);

function drawBubble(k: Kit, x: number, y: number, r: number, kind: Kind): void {
  k.circle(x, y, r, withAlpha(kindColour(k, kind), 0.42), 0.9);
  k.circle(x - r * 0.38, y + r * 0.38, r * 0.2, 'rgba(255,255,255,0.7)', 0);
  const px = Math.max(8, r * k.s * 1.15);
  if (kind === 'discount') k.text('%', k.X(x), k.Y(y) + px * 0.34, px, 'center');
  else if (kind === 'news') { for (const dy of [-0.38, 0, 0.38]) k.line([x - r * 0.42, y + dy * r, x + r * 0.42, y + dy * r], 0.55); }
  else { k.circle(x - r * 0.25, y, r * 0.22, null, 0.6); k.line([x - r * 0.03, y, x + r * 0.5, y, x + r * 0.5, y - r * 0.28], 0.6); }
}

// ---- the tents ---------------------------------------------------------------------
function wrap(k: Kit, str: string, maxW: number, px: number): string[] {
  const g = k.ctx; g.font = `500 ${px}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
  const words = str.split(/\s+/), lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (g.measureText(t).width <= maxW || !cur) cur = t; else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length > 2) { lines.length = 2; lines[1] = `${lines[1].replace(/.{2}$/, '')}…`; }
  return lines;
}

function drawTent(k: Kit, t: Tent): void {
  const c = k.c, x0 = t.x0, w = t.w, x1 = x0 + w, cx = x0 + w / 2;
  const col = [c.coral, c.yellow, t.kind === 'news' ? c.cobalt : c.lime][t.col];
  const pole = 8, peak = 12.5, n = 6;
  // back sign: the name on a board under the canopy
  k.rect(x0 + 1.6, 4.3, x1 - 1.6, 6.9, c.paper, 0.8);
  const px = Math.max(9, k.s * 1.05);
  const lines = wrap(k, t.title, (w - 4) * k.s, px);
  lines.forEach((ln, i) => k.text(ln, k.X(cx), k.Y(6.1 - i * 1.15) + px * 0.3, px, 'center'));
  // poles
  k.rect(x0, 0, x0 + 0.4, pole, c.poche, 0); k.rect(x1 - 0.4, 0, x1, pole, c.poche, 0);
  // table with a cloth in the tent's colour, and a few things on it
  k.rect(x0 + 2.2, 0, x1 - 2.2, 2.4, c.paper, 1);
  k.rect(x0 + 2.2, 1.4, x1 - 2.2, 2.4, col, 0.8);
  const things = t.kind === 'news' ? [[0.8, 1.2], [4.4, 0.7], [8, 1.5]] : [[0.8, 1.4], [4.4, 1.0], [8, 1.4]];
  for (const [dx, hh] of things) {
    if (t.kind === 'news') { k.rect(x0 + 3 + dx, 2.4, x0 + 5.6 + dx, 2.4 + hh * 0.5, c.paperShade, 0.6); k.rect(x0 + 3.3 + dx, 2.9, x0 + 5.9 + dx, 2.9 + hh * 0.4, c.paper, 0.6); }
    else { k.rect(x0 + 3 + dx, 2.4, x0 + 4.8 + dx, 2.4 + hh, c.paper, 0.7); k.rect(x0 + 3.2 + dx, 2.6, x0 + 4.6 + dx, 2.9, c.yellow, 0.4); }
  }
  // canopy in stripes, then a valance
  for (let i = 0; i < n; i++) {
    const a = x0 - 1 + ((w + 2) * i) / n, b = x0 - 1 + ((w + 2) * (i + 1)) / n;
    k.poly([a, pole, cx, peak, b, pole], i % 2 ? c.paper : col, 0.6);
  }
  k.poly([x0 - 1, pole, cx, peak, x1 + 1, pole], null, 1.2);
  for (let i = 0; i < n; i++) {
    const a = x0 - 1 + ((w + 2) * i) / n, b = x0 - 1 + ((w + 2) * (i + 1)) / n;
    k.poly([a, pole, b, pole, (a + b) / 2, pole - 1.1], i % 2 ? c.paper : col, 0.7);
  }
  k.line([x0 - 1, pole, x1 + 1, pole], 1.1);
}

function drawTents(k: Kit): void {
  const c = k.c;
  for (const t of tents) drawTent(k, t);
  // bunting between the peaks, and a banner for each group
  for (const side of [0, 1]) {
    const group = tents.filter((t) => (t.kind === 'news') === !!side);
    if (!group.length) continue;
    for (let i = 0; i + 1 < group.length; i++) {
      const ax = group[i].x0 + TENT_W / 2, bx = group[i + 1].x0 + TENT_W / 2;
      const pts: number[] = [];
      for (let u = 0; u <= 1.001; u += 0.1) pts.push(ax + (bx - ax) * u, 12.5 - 1.8 * Math.sin(Math.PI * u));
      k.line(pts, 0.5);
      for (let f = 1; f < 10; f++) {
        const u = f / 10, x = ax + (bx - ax) * u, y = 12.5 - 1.8 * Math.sin(Math.PI * u);
        k.poly([x - 0.6, y, x + 0.6, y, x, y - 1.3], [c.coral, c.yellow, c.cobalt, c.lime][f % 4], 0.4);
      }
    }
    const first = group[0].x0 + TENT_W / 2, last = group[group.length - 1].x0 + TENT_W / 2, mid = (first + last) / 2;
    const px = Math.max(9, k.s * 1.15), label = side ? 'NEWS' : 'FUNDRAISERS';
    k.line([mid - 8, 14.4, first, 12.5], 0.5); k.line([mid + 8, 14.4, last, 12.5], 0.5);
    k.rect(mid - 8, 14.4, mid + 8, 17, c.paper, 1);
    k.text(label, k.X(mid), k.Y(15.7) + px * 0.35, px, 'center');
  }
  // people milling about
  const folk: [number, number][] = [[23.5, 1], [44.5, -1], [65, 1], [165.5, 1], [186, -1], [206.5, -1]];
  folk.forEach(([x, f], i) => k.person(x + Math.sin(k.t * 0.5 + i * 2.1) * 0.8, f, 0));
}

function readTents(source: HTMLElement): Tent[] {
  const out: Tent[] = [];
  const read = (heading: string, kind: Tent['kind'], x0s: number[]): void => {
    const h = Array.from(source.querySelectorAll('h3')).find((n) => (n.textContent ?? '').trim().toLowerCase() === heading);
    let ul = h?.nextElementSibling ?? null;
    while (ul && ul.tagName !== 'UL' && ul.tagName !== 'H3') ul = ul.nextElementSibling;
    const items = ul && ul.tagName === 'UL' ? Array.from(ul.querySelectorAll('li')).slice(0, 3) : [];
    items.forEach((li, i) => {
      const title = li.querySelector('strong')?.textContent?.trim() ?? '';
      const time = li.querySelector('time')?.textContent?.trim() ?? '';
      const text = (li.textContent ?? '').replace(title, '').replace(time, '').replace(/\s+/g, ' ').trim();
      out.push({ x0: x0s[i], w: TENT_W, kind, title: title || (kind === 'news' ? 'News' : 'Fundraiser'), text, time, col: (i % 3) as 0 | 1 | 2 });
    });
  };
  read('fundraisers', 'fundraiser', [4, 25, 46]);
  read('news', 'news', [146, 167, 188]);
  return out;
}

/** The tent under a tap, in feet. */
const tentAt = (wx: number, wy: number): Tent | undefined => (wy >= -0.5 && wy <= 12 ? tents.find((t) => wx >= t.x0 - 1 && wx <= t.x0 + t.w + 1) : undefined);


// ---- the drawing ----------------------------------------------------------------
function draw(k: Kit): void {
  update(k);
  const c = k.c, g = k.ctx;
  const lo = -k.ox / k.s, hi = (k.w - k.ox) / k.s;
  const earthBottom = -((k.h - k.gy) / k.s) - 1;

  // grass along the ground line
  g.strokeStyle = c.tuft; g.lineWidth = k.lw(0.7); g.lineCap = 'round'; g.beginPath();
  for (let x = Math.floor(Math.max(lo, -60) / 2.4) * 2.4; x <= Math.min(hi, W + 60); x += 2.4) {
    if (x > 80 && x < 130) continue;
    for (let j = 0; j < 3; j++) {
      const hgt = 1.6 + (Math.sin(x * 12.9 + j * 3.1) + 1) * 1.4;
      const sway = Math.sin(k.t * 1.3 + x * 0.4 + j) * 0.5 + (j - 1) * 0.5;
      g.moveTo(k.X(x + j * 0.4), k.Y(0)); g.lineTo(k.X(x + j * 0.4 + sway), k.Y(hgt));
    }
  }
  g.stroke();

  // the earth, cut: hatched
  const ex0 = Math.max(lo - 4, -60), ex1 = Math.min(hi + 4, W + 60);
  g.save();
  g.beginPath(); g.rect(k.X(ex0), k.Y(0), k.X(ex1) - k.X(ex0), k.Y(earthBottom) - k.Y(0)); g.clip();
  g.fillStyle = c.ground; g.fillRect(k.X(ex0), k.Y(0), k.X(ex1) - k.X(ex0), k.Y(earthBottom) - k.Y(0));
  g.strokeStyle = c.inkSoft; g.lineWidth = Math.max(0.6, k.s * 0.06); g.beginPath();
  for (let x = Math.floor(ex0 / 3) * 3 - 80; x < ex1; x += 3) { g.moveTo(k.X(x), k.Y(0)); g.lineTo(k.X(x - earthBottom * 0.9), k.Y(earthBottom)); }
  g.stroke();
  g.restore();

  // the shaft, cut through the earth
  k.rect(SX0, FLOOR, SX1, 0, c.interior, 1.8);

  // the meet and greet: temporary tents on the ground, fundraisers on the left and news on the right
  drawTents(k);

  // the shaft: lining, then the water and the bubbles
  k.rect(SX0 - 1.5, FLOOR - 1.4, SX1 + 1.5, FLOOR, c.poche, 0);
  k.rect(SX0 - 1.5, FLOOR, SX0, 0, c.poche, 0); k.rect(SX1, FLOOR, SX1 + 1.5, 0, c.poche, 0);
  {
    const surf: number[] = [];
    for (let x = SX0; x <= SX1 + 0.01; x += 1) surf.push(x, WATER + 0.3 * Math.sin(x * 1.3 + k.t * 2));
    k.poly([SX0, FLOOR, ...surf, SX1, FLOOR], withAlpha(c.cobalt, 0.4), 0);
    k.line(surf, 0.8);
  }
  if (ripple > 0) {
    g.strokeStyle = c.inkSoft; g.lineWidth = k.lw(0.6);
    for (const d of [0, 0.25]) {
      const u = ripple - d;
      if (u <= 0 || u > 0.9) continue;
      g.beginPath(); g.ellipse(k.X(CX), k.Y(WATER), (1 + u * 6) * k.s, (0.3 + u) * 0.4 * k.s, 0, 0, Math.PI * 2); g.stroke();
    }
  }
  for (const b of bubbles) {
    const p = bubbleAt(b, k.t);
    drawBubble(k, p.x, p.y, b.r, prizes[b.prize % Math.max(1, prizes.length)]?.kind ?? 'news');
  }

  // the well head: stone ring, two posts, a gabled roof, a beam and a pulley
  k.rect(SX0 - 3, 0, SX0, 3.5, c.poche, 0); k.rect(SX1, 0, SX1 + 3, 3.5, c.poche, 0);
  for (const x of [SX0 - 5.6, SX1 + 4.4]) k.rect(x, 3.5, x + 1.2, 15, c.poche, 0);
  k.poly([82, 15, 105, 23, 128, 15], c.paper, 0);
  k.poly([82, 15, 105, 23, 105, 22.4, 82.6, 15], c.poche, 0); k.poly([128, 15, 105, 23, 105, 22.4, 127.4, 15], c.poche, 0);
  k.rect(84, 14.2, 126, 15, c.poche, 0);
  k.line([105, 14.2, 105, 13.2], 1.4);
  const wheelR = 1.7, wheelY = 12.1;
  k.circle(CX, wheelY, wheelR, c.paper, 1.3);
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI + by * 0.35; k.line([CX - wheelR * Math.cos(a), wheelY - wheelR * Math.sin(a), CX + wheelR * Math.cos(a), wheelY + wheelR * Math.sin(a)], 0.6); }

  // the bucket on its rope, with a bubble riding home if one was caught
  const hw = 4;
  k.line([CX, by + 7.2, CX, wheelY - wheelR], 0.9);
  if (caught) drawBubble(k, CX, by + 5.2, caught.r, prizes[caught.prize % prizes.length].kind);
  k.poly([CX - hw, by + 4.5, CX + hw, by + 4.5, CX + hw * 0.78, by, CX - hw * 0.78, by], c.coral, 1.2);
  for (const y of [by + 1.2, by + 3.3]) k.line([CX - hw * (0.8 + 0.2 * ((y - by) / 4.5)), y, CX + hw * (0.8 + 0.2 * ((y - by) / 4.5)), y], 0.8);
  k.line([CX - hw, by + 4.5, CX, by + 7.2, CX + hw, by + 4.5], 0.9);
  for (const b of bursts) {
    const u = b.t / 0.6;
    g.strokeStyle = b.col; g.lineWidth = k.lw(0.9);
    g.beginPath();
    for (let i = 0; i < 10; i++) { const a = (i / 10) * Math.PI * 2; const r0 = (1.4 + u * 3) * k.s, r1 = (2.4 + u * 4) * k.s; g.moveTo(k.X(b.x) + Math.cos(a) * r0, k.Y(b.y) + Math.sin(a) * r0); g.lineTo(k.X(b.x) + Math.cos(a) * r1, k.Y(b.y) + Math.sin(a) * r1); }
    g.stroke();
  }
}

// ---- the controls: drop button, prize card, the bucket's catch ---------------------------
function readPrizes(): Prize[] {
  const out: Prize[] = [];
  document.querySelectorAll<HTMLElement>('#well-bubbles li').forEach((li) => {
    const kind = (li.dataset.kind as Kind) || 'news';
    const title = li.querySelector('strong')?.textContent?.trim() ?? KIND_LABEL[kind];
    const text = (li.textContent ?? '').replace(title, '').trim();
    out.push({ kind, title, text, code: li.dataset.code });
  });
  return out.length ? out : [{ kind: 'news', title: 'News', text: 'Coming soon.' }];
}

function mount(host: Host): () => void {
  prizes = readPrizes();
  tents = readTents(host.source);
  nextPrize = 0;
  bubbles = Array.from({ length: 6 }, (_, i) => newBubble(nextPrize++, (i / 6) * 6.283));
  phase = 'idle'; pt = 0; by = REST; caught = null; bursts = []; ripple = 0; cardOpen = false; respawn = [];
  const mine: Prize[] = [];

  const mk = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
    const e = document.createElement(tag); if (cls) e.className = cls; if (text !== undefined) e.textContent = text; return e;
  };
  const dropBtn = mk('button', 'well-drop', 'Drop the bucket'); dropBtn.type = 'button';
  const pill = mk('button', 'well-pill', 'Caught: 0'); pill.type = 'button';
  const card = mk('div', 'well-card'); card.hidden = true; card.setAttribute('role', 'status');
  const bag = mk('div', 'well-bag'); bag.hidden = true;
  host.box.append(dropBtn, pill, card, bag);

  const copy = (text: string, btn: HTMLButtonElement): void => {
    const done = (): void => { const was = btn.textContent; btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = was; }, 1400); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, done); else done();
  };
  const codeRow = (p: Prize): HTMLElement => {
    const row = mk('div', 'code');
    row.appendChild(mk('code', undefined, p.code ?? ''));
    const b = mk('button', undefined, 'Copy'); b.type = 'button';
    b.addEventListener('click', () => copy(p.code ?? '', b));
    row.appendChild(b);
    return row;
  };
  const renderBag = (): void => {
    bag.textContent = '';
    bag.appendChild(mk('p', 'eyebrow', 'Caught this visit'));
    if (!mine.length) bag.appendChild(mk('p', 'empty', 'Nothing yet. Drop the bucket.'));
    for (const p of mine) {
      const row = mk('div', `item ${p.kind}`);
      row.appendChild(mk('strong', undefined, p.title));
      row.appendChild(mk('span', undefined, p.text));
      if (p.code) row.appendChild(codeRow(p));
      bag.appendChild(row);
    }
  };
  renderBag();
  pill.addEventListener('click', () => { bag.hidden = !bag.hidden; });

  let toast = 0;
  onResult = (p): void => {
    card.textContent = '';
    card.className = 'well-card';
    if (!p) {
      card.classList.add('miss');
      card.appendChild(mk('p', 'text', 'Only water. Try when a bubble is under the bucket.'));
      card.hidden = false;
      clearTimeout(toast);
      toast = window.setTimeout(() => { card.hidden = true; }, 2200);
      return;
    }
    mine.push(p);
    pill.textContent = `Caught: ${mine.length}`;
    renderBag();
    cardOpen = true;
    card.classList.add(p.kind);
    card.appendChild(mk('p', 'eyebrow', `You caught ${p.kind === 'news' ? 'news' : 'a code'}`));
    card.appendChild(mk('h3', undefined, p.title));
    card.appendChild(mk('p', 'text', p.text));
    if (p.code) card.appendChild(codeRow(p));
    const row = mk('div', 'row');
    if (p.kind === 'news') { const m = mk('button', undefined, 'More news'); m.type = 'button'; m.addEventListener('click', () => { closeCard(); host.go(2); }); row.appendChild(m); }
    const again = mk('button', 'primary', 'Drop again'); again.type = 'button';
    again.addEventListener('click', () => { closeCard(); drop(); });
    row.appendChild(again);
    card.appendChild(row);
    card.hidden = false;
    again.focus({ preventScroll: true });
  };
  const closeCard = (): void => { cardOpen = false; card.hidden = true; };

  const showTent = (t: Tent): void => {
    clearTimeout(toast);
    card.textContent = '';
    card.className = `well-card ${t.kind === 'news' ? 'news' : 'discount'}`;
    card.appendChild(mk('p', 'eyebrow', t.kind === 'news' ? `News${t.time ? ` · ${t.time}` : ''}` : 'Fundraiser'));
    card.appendChild(mk('h3', undefined, t.title));
    card.appendChild(mk('p', 'text', t.text));
    const row = mk('div', 'row');
    const x = mk('button', 'primary', 'Close'); x.type = 'button';
    x.addEventListener('click', closeCard);
    if (t.kind === 'fundraiser') { const link = mk('button', undefined, 'Donate: link to come'); link.type = 'button'; link.disabled = true; row.append(link); }
    row.append(x);
    card.appendChild(row);
    card.hidden = false;
    cardOpen = false;
  };
  let tentRoom = -1;
  tentHandler = (wx, wy): boolean => { const t = tentAt(wx, wy); if (t) { showTent(t); tentRoom = host.room(); } return !!t; };
  const roomWatch = window.setInterval(() => { if (tentRoom >= 0 && host.room() !== tentRoom) { tentRoom = -1; if (!cardOpen) card.hidden = true; } }, 250);

  const request = (): void => {
    if (host.room() !== 1) host.go(1);
    if (cardOpen) { closeCard(); }
    card.hidden = true;
    drop();
  };
  dropBtn.addEventListener('click', request);
  const key = (e: KeyboardEvent): void => {
    if (e.key === ' ' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); request(); }
  };
  addEventListener('keydown', key);
  tapHandler = (): boolean => { request(); return true; };

  return (): void => {
    removeEventListener('keydown', key);
    clearTimeout(toast); clearInterval(roomWatch);
    tapHandler = () => false; tentHandler = () => false;
    onResult = () => {};
    dropBtn.remove(); pill.remove(); card.remove(); bag.remove();
  };
}
let tapHandler: () => boolean = () => false;
let tentHandler: (wx: number, wy: number) => boolean = () => false;

export const wellSection: SectionDef = {
  id: 'well',
  width: W,
  height: 64,
  rooms: [
    { name: 'Fundraisers', heading: 'Fundraisers', x0: 0, x1: 70, lead: true },
    { name: 'Play', heading: 'Play', x0: 70, x1: 140 },
    { name: 'News', heading: 'News', x0: 140, x1: 210, lead: true },
  ],
  start: 1,
  fill: [0.7, 0.5],
  ground: [0.34, 0.26],
  capBottom: true,
  draw,
  mount,
  onTap: (wx, wy) => (tentHandler(wx, wy) ? true : wx >= 70 && wx < 140 ? tapHandler() : false),
};
